import donutImage from "@assets/images/donut.png";
import { useUpdateOnboardingUser } from "@features/onboarding/mutations/useUpdateOnboardingUser";
import { BIO_TEXT_MAX, type BioContent, flattenBio, resolveBioContent } from "@fitties/shared";
import logger from "@logger";
import { BioPicker } from "@shared/components/BioPicker";
import { useCurrentUser } from "@shared/queries";
import clsx from "clsx";
import { useState } from "react";

interface BioFormProps {
  onSaved?: () => void;
}

export default function BioForm({ onSaved }: BioFormProps) {
  const { user } = useCurrentUser();
  const { mutate: updateOnboardingUser, isPending: isSubmitting, error: mutationError } = useUpdateOnboardingUser();
  const [bioContent, setBioContent] = useState<BioContent | null>(() =>
    resolveBioContent({ bio: user?.bio ?? null, bioContent: user?.bioContent ?? null })
  );
  const [bioError, setBioError] = useState<string | undefined>();

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!bioContent) {
      setBioError("Bio is required");
      return;
    }
    if (flattenBio(bioContent).length > BIO_TEXT_MAX) {
      setBioError("Bio must be 150 characters or less");
      return;
    }
    setBioError(undefined);
    const payload = { bio: flattenBio(bioContent), bioContent };
    logger.info("📝 BioForm submitting data:", payload);
    updateOnboardingUser(payload, {
      onSuccess: (result) => {
        logger.info("✅ BioForm mutation successful:", result);
        onSaved?.();
      },
      onError: (error) => {
        logger.error("❌ BioForm mutation failed:", error);
      },
    });
  };

  return (
    <div data-testid="bio-form-root" className="h-full min-h-0 flex flex-col text-center font-mono">
      <div data-testid="bio-form-content" className="flex-1 overflow-y-auto space-y-4 pb-2">
        <h1 data-testid="bio-form-heading" className="text-2xl font-bold">
          Tell us about yourself
        </h1>

        <p data-testid="bio-form-description" className="text-black/80 text-md">
          Pick a line — don't overthink it
        </p>

        <form data-testid="bio-form" onSubmit={handleFormSubmit} className="mx-auto w-full space-y-4" noValidate>
          {mutationError && (
            <div data-testid="bio-form-error" className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p data-testid="bio-form-error-message" className="text-red-600 text-sm">
                {mutationError.message}
              </p>
            </div>
          )}

          <BioPicker
            value={bioContent}
            onChange={(next) => {
              setBioContent(next);
              if (next) {
                setBioError(undefined);
              }
            }}
            {...(bioError ? { error: bioError } : {})}
          />
        </form>

        <div data-testid="bio-form-mascot" className="flex justify-end">
          <img
            data-testid="bio-form-mascot-image"
            src={donutImage}
            alt="Cute donut mascot"
            className="w-32 h-32 object-contain"
          />
        </div>
      </div>

      <div data-testid="bio-form-footer" className="sticky bottom-0 z-10 bg-lavender-400 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          onClick={handleFormSubmit}
          className={clsx("w-full px-8 py-4 text-lg rounded-lg font-medium transition-colors", {
            "bg-pink-600 text-white hover:bg-pink-700": !isSubmitting,
            "bg-gray-300 text-gray-500 cursor-not-allowed": isSubmitting,
          })}
          data-testid="submit-button"
        >
          <span data-testid="bio-form-submit-label">{isSubmitting ? "Saving..." : "Continue"}</span>
        </button>
      </div>
    </div>
  );
}
